import { useMemo } from 'react';
import type { UserDocType } from '@ast-data/shared';
import { useUserDirectory } from './userDirectory';
import { isEmployedDuring, type EmploymentWindow } from './employment';

/**
 * EVERY user account, deactivated ones included — what anything that RESOLVES
 * an existing record takes. A name or an id on a record written months ago
 * still means the person it meant then, so deactivating an account must never
 * unlink them: see `useAccountResolver` and `useCrewKeyResolver`.
 *
 * Read from the app's one user directory (see userDirectory), like every hook
 * here: no caller opens a query of its own.
 */
export function useAllUsers(): UserDocType[] {
    return useUserDirectory().users;
}

/**
 * Every account, ACTIVE ONES FIRST. For the readers that take a LIST and let
 * the first fit win — the import matchers, the crew key resolver: a deactivated
 * account must still be reachable (an old sheet names somebody who has since
 * left), while a name fitting an active and a deactivated one means the person
 * still here.
 */
export function useUsersActiveFirst(): UserDocType[] {
    return useUserDirectory().usersActiveFirst;
}

/**
 * All ACTIVE (non-disabled) user accounts — the single source for user pickers,
 * so a deactivated user never shows up as an option. Views that must see EVERY
 * user take {@link useAllUsers}: the admin Users grid, the label resolvers and
 * the account link itself.
 *
 * Given a window — the record's day — only those employed during it (see
 * ./employment).
 */
export function useActiveUsers(window?: EmploymentWindow): UserDocType[] {
    const { activeUsers, profileById } = useUserDirectory();
    const from = window?.from;
    const to = window?.to;
    return useMemo(
        () =>
            from === undefined || to === undefined
                ? activeUsers
                : activeUsers.filter((user) =>
                      isEmployedDuring(profileById.get(user.id), { from, to })
                  ),
        [activeUsers, profileById, from, to]
    );
}
